import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Alert,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  IconButton,
  Avatar,
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem, GridSortModel, GridPaginationModel } from '@mui/x-data-grid';
import { useThemeMode } from '../../../contexts/ThemeModeContext';
import { dataGridHeaderSx } from '../../../theme/themes';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import UploadIcon from '@mui/icons-material/Upload';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import { useAuth } from '../../../contexts/AuthContext';
import ImageViewerDialog from '../ImageViewerDialog';
import { createConcurrencyLimiter } from '../../../utils/concurrentLimit';

type DeviceEvent = {
  id: string;
  deviceId: string;
  deviceName?: string;
  name: string;
  eventType: string;
  dateCreated: string;
};

interface RawDataTabProps {
  deviceId: string;
  includeChildren?: boolean;
}

const RawDataTab: React.FC<RawDataTabProps> = ({ deviceId, includeChildren = false }) => {
  const auth = useAuth();
  const { mode } = useThemeMode();
  const accessToken = auth.tokens?.accessToken;
  const [events, setEvents] = useState<DeviceEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'dateCreated', sort: 'desc' },
  ]);

  const authHeader = useMemo(() => ({ Authorization: `Bearer ${accessToken}` }), [accessToken]);
  
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [deleteAllConfirmOpen, setDeleteAllConfirmOpen] = useState(false);
  const [deletePageConfirmOpen, setDeletePageConfirmOpen] = useState(false);
  const [deletingPage, setDeletingPage] = useState(false);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState('Manual');
  const [formFiles, setFormFiles] = useState<File[]>([]);
  
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadingEventId, setUploadingEventId] = useState<string | null>(null);
  
  // Viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerEventId, setViewerEventId] = useState<string | null>(null);
  const [viewerImages, setViewerImages] = useState<string[]>([]);
  const [viewerIndex, setViewerIndex] = useState(0);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const skip = paginationModel.page * paginationModel.pageSize;
      const take = paginationModel.pageSize;
      const sortBy = sortModel[0]?.field || 'dateCreated';
      const sortOrder = sortModel[0]?.sort || 'desc';

      const params = new URLSearchParams({
        skip: skip.toString(),
        take: take.toString(),
        sortBy,
        sortOrder,
      });

      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (includeChildren) params.append('includeChildren', 'true');

      const res = await fetch(
        `/api/devices/${deviceId}/events?${params}`, 
        { headers: authHeader }
      );
      if (!res.ok) throw new Error('Failed to fetch events');
      
      const responseData = await res.json();
      const data: DeviceEvent[] = responseData.data || [];
      const total = responseData.total || 0;
      
      setEvents(data);
      setTotalCount(total);
      
      // Load thumbnails for all events (use the event's own deviceId for child events)
      data.forEach(ev => loadThumb(ev.id, ev.deviceId));
    } catch (e: any) {
      setError(e.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, paginationModel, sortModel, startDate, endDate, includeChildren]);

  const loadThumb = async (eventId: string, ownerDeviceId?: string) => {
    const dId = ownerDeviceId || deviceId;
    try {
      const res = await fetch(`/api/devices/${dId}/events/${eventId}/latest-image/thumbnail?maxWidth=80`, { headers: authHeader });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setThumbs((p) => ({ ...p, [eventId]: url }));
    } catch {
      // ignore
    }
  };

  const handleAdd = () => {
    setFormName('');
    setFormType('Manual');
    setFormFiles([]);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setFormName('');
    setFormType('Manual');
    setFormFiles([]);
  };

  const handleDialogSave = async () => {
    setError(null);
    setSuccess(null);
    
    if (!formType.trim()) {
      setError('Event type is required');
      return;
    }

    try {
      // Create the event first
      const res = await fetch(`/api/devices/${deviceId}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({ name: formName, eventType: formType }),
      });
      if (!res.ok) throw new Error('Failed to create event');
      
      const newEvent = await res.json();
      
      // Upload files if any
      if (formFiles.length > 0) {
        for (const file of formFiles) {
          const form = new FormData();
          form.append('file', file);
          const uploadRes = await fetch(`/api/devices/${deviceId}/events/${newEvent.id}/upload-image`, {
            method: 'POST',
            headers: authHeader,
            body: form,
          } as any);
          if (!uploadRes.ok) {
            console.warn(`Failed to upload file: ${file.name}`);
          }
        }
      }
      
      setSuccess(`Event created successfully${formFiles.length > 0 ? ` with ${formFiles.length} file(s)` : ''}`);
      handleDialogClose();
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to create event');
    }
  };

  const handleDelete = async (id: string, ownerDeviceId?: string) => {
    setError(null);
    setSuccess(null);
    const dId = ownerDeviceId || deviceId;
    try {
      const res = await fetch(`/api/devices/${dId}/events/${id}`, {
        method: 'DELETE',
        headers: authHeader,
      });
      if (!res.ok) throw new Error('Failed to delete event');
      setSuccess('Event deleted successfully');
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to delete event');
    }
  };

  const handleUploadClick = (eventId: string) => {
    setUploadingEventId(eventId);
    setUploadDialogOpen(true);
  };

  const handleUploadDialogClose = () => {
    setUploadDialogOpen(false);
    setUploadingEventId(null);
  };

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || !uploadingEventId) return;
    
    try {
      setError(null);
      let uploadedCount = 0;
      
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append('file', file);
        const res = await fetch(`/api/devices/${deviceId}/events/${uploadingEventId}/upload-image`, {
          method: 'POST',
          headers: authHeader,
          body: form,
        } as any);
        if (res.ok) {
          uploadedCount++;
        }
      }
      
      setSuccess(`${uploadedCount} file(s) uploaded successfully`);
      handleUploadDialogClose();
      loadThumb(uploadingEventId);
    } catch (e: any) {
      setError(e.message || 'Upload failed');
    }
  };

  const handleViewImages = async (eventId: string, ownerDeviceId?: string) => {
    const dId = ownerDeviceId || deviceId;
    try {
      const res = await fetch(`/api/devices/${dId}/events/${eventId}/images?skip=0&take=10`, { headers: authHeader });
      if (!res.ok) return;
      const items: { id: string }[] = await res.json();
      
      const urls: string[] = [];
      for (const it of items) {
        const r = await fetch(`/api/devices/${dId}/events/${eventId}/images/${it.id}/content?maxWidth=600`, { headers: authHeader });
        if (r.ok) {
          const b = await r.blob();
          urls.push(URL.createObjectURL(b));
        }
      }
      
      setViewerImages(urls);
      setViewerEventId(eventId);
      setViewerIndex(0);
      setViewerOpen(true);
    } catch {
      // ignore
    }
  };

  const handleViewerClose = () => {
    setViewerOpen(false);
    setViewerEventId(null);
    setViewerImages([]);
    setViewerIndex(0);
  };

  const handleViewerNext = () => {
    if (viewerImages.length > 0) {
      setViewerIndex((prev) => (prev + 1) % viewerImages.length);
    }
  };

  const handleViewerPrev = () => {
    if (viewerImages.length > 0) {
      setViewerIndex((prev) => (prev - 1 + viewerImages.length) % viewerImages.length);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'thumbnail',
      headerName: '',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const thumb = thumbs[params.row.id];
        const eventType = params.row.eventType?.toLowerCase();
        
        // Parse temperature from event name if it's a temperature event
        let temperatureColor = undefined;
        if (eventType === 'temperature' && params.row.name) {
          try {
            const parsed = JSON.parse(params.row.name);
            if (parsed?.data?.temperature !== undefined) {
              const temp = parsed.data.temperature;
              if (temp < 60) {
                temperatureColor = '#4caf50'; // Green
              } else if (temp <= 80) {
                temperatureColor = '#ff9800'; // Yellow/Orange
              } else {
                temperatureColor = '#f44336'; // Red
              }
            }
          } catch {
            // Not JSON or no temperature data
          }
        }
        
        return thumb ? (
          <Avatar
            src={thumb}
            variant="rounded"
            sx={{ width: 64, height: 64, cursor: 'pointer' }}
            onClick={(e) => { e.stopPropagation(); handleViewImages(params.row.id, params.row.deviceId); }}
            onDoubleClick={(e) => { e.stopPropagation(); handleViewImages(params.row.id, params.row.deviceId); }}
          />
        ) : (
          <Avatar variant="rounded" sx={{ width: 64, height: 64, bgcolor: temperatureColor || 'grey.400' }}>
            {eventType === 'temperature' ? <ThermostatIcon /> : <ImageIcon />}
          </Avatar>
        );
      },
    },
    ...(includeChildren ? [{
      field: 'deviceName',
      headerName: 'Device',
      width: 160,
      renderCell: (params: any) => (
        <Box sx={{ fontSize: '0.8rem', color: 'primary.main', fontWeight: 500 }}>
          {params.row.deviceName || params.row.deviceId?.slice(0, 8)}
        </Box>
      ),
    } as GridColDef] : []),
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'eventType',
      headerName: 'Type',
      width: 150,
    },
    {
      field: 'dateCreated',
      headerName: 'Created',
      width: 180,
      renderCell: (params) => {
        const value = params.row.dateCreated || params.row.DateCreated;
        if (!value) return 'No date';
        const date = new Date(value);
        if (isNaN(date.getTime())) return 'Invalid Date';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      getActions: (params: any) => [
        <GridActionsCellItem
          icon={<UploadIcon />}
          label="Upload"
          onClick={() => handleUploadClick(params.row.id)}
        />,
        <GridActionsCellItem
          icon={<ImageIcon />}
          label="View Images"
          onClick={() => handleViewImages(params.row.id, params.row.deviceId)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDelete(params.row.id, params.row.deviceId)}
        />,
      ],
    },
  ];

  // Deleting every event at once is for admins (the API refuses anyone else)
  const isAdmin = auth.hasRole('DMO_ADMIN');

  // The events on this page, each from the device it belongs to (a child's, with children included)
  const handleDeletePage = async () => {
    setDeletingPage(true);
    setError(null);
    setSuccess(null);
    try {
      const limitRequests = createConcurrencyLimiter(6);
      const results = await Promise.allSettled(
        events.map((ev) => limitRequests(async () => {
          const res = await fetch(`/api/devices/${ev.deviceId || deviceId}/events/${ev.id}`, {
            method: 'DELETE', headers: authHeader,
          });
          if (!res.ok) throw new Error('Delete failed');
        }))
      );
      const failedCount = results.filter((r) => r.status === 'rejected').length;
      if (failedCount > 0) {
        setError(`Failed to delete ${failedCount} of ${events.length} event(s)`);
      } else {
        setSuccess(`Deleted ${events.length} event${events.length !== 1 ? 's' : ''}`);
      }
      // The page may now be past the end
      if (paginationModel.page > 0) {
        setPaginationModel((m) => ({ ...m, page: 0 }));
      } else {
        await load();
      }
    } catch {
      setError('Failed to delete this page of events');
    } finally {
      setDeletingPage(false);
      setDeletePageConfirmOpen(false);
    }
  };

  const handleDeleteAll = async () => {
    try {
      const res = await fetch(
        `/api/devices/${deviceId}/events/all${includeChildren ? '?includeChildren=true' : ''}`,
        { method: 'DELETE', headers: authHeader }
      );

      if (!res.ok) {
        if (res.status === 403) {
          setError('Only administrators can delete all events');
        } else {
          throw new Error('Delete all failed');
        }
        setDeleteAllConfirmOpen(false);
        return;
      }

      setSuccess('All events deleted successfully');
      await load();
      setDeleteAllConfirmOpen(false);
    } catch (err) {
      console.error('Error deleting all events:', err);
      setError('Failed to delete all events');
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Raw Event Data</Typography>
        <Stack direction="row" spacing={1}>
          {events.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeletePageConfirmOpen(true)}
              size="small"
            >
              Delete Page
            </Button>
          )}
          {isAdmin && totalCount > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteAllConfirmOpen(true)}
              size="small"
            >
              Delete All
            </Button>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
            Add Event
          </Button>
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          type="date"
          label="Start Date"
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
            setPaginationModel({ ...paginationModel, page: 0 });
          }}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          type="date"
          label="End Date"
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value);
            setPaginationModel({ ...paginationModel, page: 0 });
          }}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        {(startDate || endDate) && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setStartDate('');
              setEndDate('');
              setPaginationModel({ ...paginationModel, page: 0 });
            }}
          >
            Clear Filters
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={events}
          columns={columns}
          loading={loading}
          rowCount={totalCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          paginationMode="server"
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          pageSizeOptions={[10, 25, 50, 100]}
          sx={dataGridHeaderSx(mode)}
          disableRowSelectionOnClick
          rowHeight={80}
        />
      </Box>

      {/* Add Event Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add Event</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Event Name"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              fullWidth
              helperText="Optional - leave blank for auto-generated name"
            />
            <TextField
              label="Event Type"
              value={formType}
              onChange={(e) => setFormType(e.target.value)}
              fullWidth
              required
              helperText="e.g., Manual, Detection, Alert"
            />
            <Box>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadIcon />}
                fullWidth
              >
                Add Files (Images/Videos)
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,video/mp4"
                  multiple
                  hidden
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setFormFiles((prev) => [...prev, ...files]);
                  }}
                />
              </Button>
              {formFiles.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Selected files:
                  </Typography>
                  {formFiles.map((file, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="body2">{file.name}</Typography>
                      <IconButton
                        size="small"
                        onClick={() => setFormFiles((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleDialogSave} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={handleUploadDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Images/Videos</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1, alignItems: 'center' }}>
            <Button
              component="label"
              variant="contained"
              startIcon={<UploadIcon />}
              fullWidth
            >
              Choose Files
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg,video/mp4"
                multiple
                hidden
                onChange={(e) => {
                  const files = e.target.files;
                  if (files) handleFileUpload(files);
                }}
              />
            </Button>
            <Typography variant="caption" color="text.secondary">
              Supported formats: PNG, JPG, JPEG, MP4 (multiple files allowed)
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Confirmation Dialog */}
      <Dialog open={deleteAllConfirmOpen} onClose={() => setDeleteAllConfirmOpen(false)}>
        <DialogTitle>Delete All Events</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>every event</strong> for this device
            {includeChildren ? ' and its child devices' : ''}{startDate || endDate ? ', not only those in the dates shown' : ''}?
            This action cannot be undone and will also delete their pictures, and the container or
            number-plate sightings logged from them.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAllConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteAll} color="error" variant="contained">
            Delete All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Page Confirmation Dialog */}
      <Dialog open={deletePageConfirmOpen} onClose={() => !deletingPage && setDeletePageConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete This Page?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will permanently delete the {events.length} event{events.length !== 1 ? 's' : ''} shown on this page,
            with their pictures and the container or number-plate sightings logged from them. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePageConfirmOpen(false)} disabled={deletingPage}>Cancel</Button>
          <Button onClick={handleDeletePage} color="error" variant="contained" disabled={deletingPage}>
            {deletingPage ? 'Deleting…' : 'Delete Page'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Viewer Dialog */}
      <ImageViewerDialog
        open={viewerOpen}
        onClose={handleViewerClose}
        imageUrl={viewerImages.length > 0 ? viewerImages[viewerIndex] : ''}
        eventName={events.find(e => e.id === viewerEventId)?.name}
        currentIndex={viewerIndex}
        totalImages={viewerImages.length}
        onNext={viewerImages.length > 1 ? handleViewerNext : undefined}
        onPrevious={viewerImages.length > 1 ? handleViewerPrev : undefined}
      />
    </Box>
  );
};

export default RawDataTab;
